import React from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Calculator, Target, BarChart3, Smartphone, Users, Shield } from 'lucide-react';

const KeyFeatures = () => {
  const keyFeatures = [
    {
      icon: <Calculator className="h-8 w-8 text-primary" />,
      title: "Accurate Unit Cost Calculation",
      description: "Easily identify the unit cost of each project delivery, even when employees work across multiple projects with varying salary brackets and overhead costs."
    },
    {
      icon: <Target className="h-8 w-8 text-primary" />,
      title: "Daily Project Assignment & Tracking",
      description: "Managers can assign projects daily, and employees can log their activities through a user-friendly web application."
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-primary" />,
      title: "Comprehensive Reporting",
      description: "Access detailed reports on daily activities, helping management analyze productivity and project progress."
    },
    {
      icon: <Smartphone className="h-8 w-8 text-primary" />,
      title: "Mobile Attendance & Effort Tracking",
      description: "Employees can mark attendance, view holidays, and track their time spent on tasks through a convenient mobile app."
    },
    {
      icon: <Users className="h-8 w-8 text-primary" />,
      title: "Enhanced Project Management",
      description: "Streamline project management with clear visibility into costs, and employee contributions."
    },
    {
      icon: <Shield className="h-8 w-8 text-primary" />,
      title: "Secure & Private",
      description: "All sensitive data is encrypted and stored securely. Client has full control over their database with no external access."
    }
  ];

  return (
    <section className="py-20 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            EfforTrak -
            <span className="text-gradient-primary block">
              Key Features
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Everything you need to manage projects, track costs, and boost team productivity in one powerful platform
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {keyFeatures.map((feature, index) => (
            <Card key={index} className="group hover-lift border-0 shadow-card bg-card/60 backdrop-blur-sm animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
              <CardContent className="p-8 space-y-6">
                <div className="flex items-start justify-between">
                  <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                    {feature.icon}
                  </div>
                  <CheckCircle className="h-5 w-5 text-accent" />
                </div>
                
                <div className="space-y-3">
                  <h3 className="text-xl font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
                
                {/* Hover effect indicator */}
                <div className="w-0 h-0.5 bg-gradient-primary group-hover:w-full transition-all duration-500 ease-out"></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-20 text-center animate-fade-in">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-6 py-3 rounded-full border border-accent/20 mb-6">
            <CheckCircle className="h-5 w-5" />
            <span className="font-semibold">All features included in every plan</span>
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
            Ready to transform your project management?
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of teams already using EfforTrak to streamline their workflow and reduce costs
          </p>
        </div>
      </div>
    </section>
  );
};

export default KeyFeatures;